import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LmTooltipDirective } from '../../components/tooltip/tooltip.directive';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSortByPipe } from '@rcs/ers-elementals';
import { ErsClickableDirective } from '@rcs/shared';

export interface LeaveTypeOption {
  value: string;
  label: string;
}

export interface UploadLeavePolicyDialogData {
  leaveTypes?: LeaveTypeOption[];
}

export interface UploadLeavePolicyDialogResult {
  files: File[];
  leaveTypes: string[];
}

@Component({
  selector: 'ers-upload-leave-policy-dialog',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    LmTooltipDirective,
    ErsSortByPipe,
   ErsClickableDirective],
  templateUrl: './upload-leave-policy-dialog.component.html',
  styleUrls: ['./upload-leave-policy-dialog.component.scss'],
})
export class UploadLeavePolicyDialogComponent implements OnInit {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  readonly SELECT_ALL = '__SELECT_ALL__';

  leaveTypes: LeaveTypeOption[] = [];
  selectedLeaveTypes: string[] = [];
  selectedFiles: File[] = [];
  isDragging = false;

  constructor(
    private dialogRef: MatDialogRef<UploadLeavePolicyDialogComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: UploadLeavePolicyDialogData | null
  ) {}

  ngOnInit(): void {
    this.leaveTypes = this.data?.leaveTypes ?? [
      { value: 'casual', label: 'Casual Leave' },
      { value: 'sick', label: 'Sick Leave' },
      { value: 'maternity', label: 'Maternity Leave' },
      { value: 'compoff', label: 'Comp Off Leave' },
    ];
    // Default: pre-select the first leave type
    if (this.leaveTypes.length > 0) {
      this.selectedLeaveTypes = [this.leaveTypes[0].value];
    }
  }

  get isAllSelected(): boolean {
    return this.realSelected.length === this.leaveTypes.length;
  }

  get realSelected(): string[] {
    return this.selectedLeaveTypes.filter((v) => v !== this.SELECT_ALL);
  }

  onModelChange(newValues: string[]): void {
    const hasAll = newValues.includes(this.SELECT_ALL);
    if (hasAll) {
      const currentReal = newValues.filter((v) => v !== this.SELECT_ALL);
      const shouldSelectAll = currentReal.length < this.leaveTypes.length;
      this.selectedLeaveTypes = shouldSelectAll
        ? this.leaveTypes.map((lt) => lt.value)
        : [];
    }
  }

  onDragOver(e: DragEvent): void {
    e.preventDefault();
    this.isDragging = true;
  }
  onDragLeave(): void {
    this.isDragging = false;
  }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.isDragging = false;
    const dropped = Array.from(e.dataTransfer?.files ?? []);
    this.addFiles(dropped);
  }

  onDropzoneClick(): void {
    this.fileInputRef.nativeElement.click();
  }

  onFileSelected(e: Event): void {
    const files = Array.from((e.target as HTMLInputElement).files ?? []);
    this.addFiles(files);
    // Reset input so same file can be re-added if removed
    this.fileInputRef.nativeElement.value = '';
  }

  private addFiles(files: File[]): void {
    for (const file of files) {
      const alreadyAdded = this.selectedFiles.some(
        (f) => f.name === file.name && f.size === file.size
      );
      if (!alreadyAdded) {
        this.selectedFiles = [...this.selectedFiles, file];
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onUpload(): void {
    if (this.selectedFiles.length === 0) return;
    const result: UploadLeavePolicyDialogResult = {
      files: this.selectedFiles,
      leaveTypes: this.realSelected,
    };
    this.dialogRef.close(result);
  }
}
